/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type CSSProperties, memo, type ReactNode, useEffect, useRef, useState } from 'react';

import { cssMap, keyframes } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';

// Animation phase timing constants
const PHASE_1_DURATION_MS = 400; // Phase 1: Grow from 0 to 100% (stretching from left to right)
const PHASE_2_DURATION_MS = 600; // Phase 2: Shrink from 100% to content width (edge moving from left to right)
const PHASE_3_DURATION_MS = 600; // Phase 3: Width stays at content width, isBold transitions to false
const PHASE_2_TRANSITION_DELAY_MS = 1; // Delay before phase 2 transition starts
const PHASE_3_BOLD_TRANSITION_DELAY_MS = 6; // Delay before isBold transitions to false in phase 3
const WIDTH_MEASUREMENT_BUFFER_PX = 2; // Buffer added to measured width to prevent truncation

// Animation timing function constants
const PHASE_1_TIMING_FUNCTION = 'cubic-bezier(0.6, 0.01, 0.8, 0.6)'; // Phase 1 easing curve
const PHASE_2_TIMING_FUNCTION = 'cubic-bezier(0.33, 1, 0.68, 1)'; // Phase 2 easing curve

// Total animation time: PHASE_1_DURATION_MS + PHASE_2_DURATION_MS + PHASE_3_DURATION_MS
const DEFAULT_INITIAL_ANIMATION_DELAY = 2000; // ms - default delay before animation starts

const widthAnimationPhase1 = keyframes({
	'0%': {
		width: '0%',
	},
	'100%': {
		width: '100%',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'flex',
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: '100%',
		minWidth: 0,
		alignSelf: 'stretch',
	},
	wrapperPhase1: {
		justifyContent: 'flex-start',
	},
	wrapperPhase2: {
		justifyContent: 'flex-end',
	},
	animatedContainer: {
		width: '0%',
		overflow: 'hidden',
		display: 'flex',
	},
	animatedContainerHidden: {
		opacity: 0,
		visibility: 'hidden',
	},
	animatedContainerAnimating: {
		// Animation delay will be set dynamically via inline style
		animation: `${widthAnimationPhase1} ${PHASE_1_DURATION_MS}ms ${PHASE_1_TIMING_FUNCTION} forwards`,
	},
	animatedContainerPhase2Start: {
		width: '100%',
		animation: 'none', // Stop the phase 1 animation so transition can work
	},
	animatedContainerPhase2: {
		width: 'var(--content-width, 100%)',
		transition: `width ${PHASE_2_DURATION_MS}ms ${PHASE_2_TIMING_FUNCTION} ${PHASE_2_TRANSITION_DELAY_MS}ms`,
	},
	animatedContainerPhase3: {
		width: 'var(--content-width, 100%)',
	},
	animatedContainerComplete: {
		width: 'auto',
	},
});

export type ThemeAppearance = 'default' | 'inprogress' | 'moved' | 'new' | 'removed' | 'success';

export interface AnimatedLozengeProps {
	/**
	 * The appearance type to transition to after animation.
	 */
	appearance?: ThemeAppearance;

	/**
	 * Elements to be rendered inside the lozenge. This should ideally be just a word or two.
	 */
	children?: ReactNode;

	/**
	 * Initial delay in milliseconds before the animation starts. Defaults to 2000ms.
	 */
	initialAnimationDelay?: number;

	/**
	 * Style customization to apply to the badge. Only `backgroundColor` and `color` are supported.
	 */
	style?: Pick<CSSProperties, 'backgroundColor' | 'color'>;

	/**
	 * A `testId` prop is provided for specified elements, which is a unique
	 * string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests
	 */
	testId?: string;
}

/**
 * AnimatedLozenge - A lozenge component that animates on mount with a three-phase animation:
 * Initial delay: configurable before animation starts (defaults to DEFAULT_INITIAL_ANIMATION_DELAY)
 * Phase 1 (PHASE_1_DURATION_MS): Width grows from 0 to 100% (stretching from left to right) with `inProgress` appearance and `isBold` true, using PHASE_1_TIMING_FUNCTION curve.
 * Phase 2 (PHASE_2_DURATION_MS + PHASE_2_TRANSITION_DELAY_MS delay): Width shrinks from 100% to content width (edge moving from left to right), `isBold` remains true, using PHASE_2_TIMING_FUNCTION curve.
 * Phase 3 (PHASE_3_DURATION_MS + PHASE_3_BOLD_TRANSITION_DELAY_MS delay): Width stays at content width, `isBold` transitions to false, using ease-in timing function.
 * Total animation time: initialAnimationDelay + PHASE_1_DURATION_MS + PHASE_2_DURATION_MS + PHASE_3_DURATION_MS
 */
const AnimatedLozenge = memo(
	({
		children,
		testId,
		appearance = 'default',
		initialAnimationDelay = DEFAULT_INITIAL_ANIMATION_DELAY,
		style,
	}: AnimatedLozengeProps) => {
		const [phase, setPhase] = useState<'phase1' | 'phase2' | 'phase3' | 'complete'>('phase1');
		const [contentWidth, setContentWidth] = useState<number | null>(null);
		const [isReadyToAnimate, setIsReadyToAnimate] = useState(false);
		const [shouldTransitionBold, setShouldTransitionBold] = useState(false);
		const [shouldStartPhase2Transition, setShouldStartPhase2Transition] = useState(false);
		const [hasStartedAnimation, setHasStartedAnimation] = useState(false);
		const animatedContainerRef = useRef<HTMLDivElement>(null);
		const lozengeWrapperRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			// Measure the natural content width after initial render
			requestAnimationFrame(() => {
				if (lozengeWrapperRef.current) {
					// Find the Lozenge's first span element
					const lozengeSpan: HTMLSpanElement | null =
						lozengeWrapperRef.current.querySelector('span');
					if (lozengeSpan) {
						// Temporarily remove all width constraints to measure natural width
						const originalWidth = lozengeSpan.style.width;
						const originalMaxWidth = lozengeSpan.style.maxWidth;
						const originalDisplay = lozengeSpan.style.display;
						const originalPosition = lozengeSpan.style.position;
						const originalVisibility = lozengeSpan.style.visibility;
						const originalTop = lozengeSpan.style.top;
						const originalLeft = lozengeSpan.style.left;

						// Temporarily move off-screen and remove ALL constraints
						lozengeSpan.style.position = 'absolute';
						lozengeSpan.style.visibility = 'hidden';
						lozengeSpan.style.width = 'auto';
						lozengeSpan.style.maxWidth = 'none';
						lozengeSpan.style.display = 'inline-flex';
						lozengeSpan.style.top = '-9999px';
						lozengeSpan.style.left = '-9999px';
						lozengeSpan.style.minWidth = 'auto';

						// Remove maxWidth from inner text span
						const textSpan = lozengeSpan.querySelector('span') as HTMLElement;
						const originalTextMaxWidth = textSpan?.style.maxWidth || '';
						const originalTextWidth = textSpan?.style.width || '';
						const originalTextMinWidth = textSpan?.style.minWidth || '';
						if (textSpan) {
							textSpan.style.maxWidth = 'none';
							textSpan.style.width = 'auto';
							textSpan.style.minWidth = 'auto';
						}

						// Force reflow multiple times to ensure styles are applied
						void lozengeSpan.offsetHeight;
						void textSpan?.offsetHeight;
						void lozengeSpan.offsetHeight;
						void textSpan?.offsetHeight;

						// Measure using scrollWidth to get the full content width (including any overflow)
						// Use the maximum of scrollWidth and offsetWidth to ensure we get the full width
						// Add a small buffer to account for rounding errors and ensure no truncation
						const measuredWidth = Math.max(
							lozengeSpan.scrollWidth,
							lozengeSpan.offsetWidth,
							textSpan ? Math.max(textSpan.scrollWidth, textSpan.offsetWidth) : 0,
						);
						const naturalWidth = Math.ceil(measuredWidth) + WIDTH_MEASUREMENT_BUFFER_PX;

						// Restore original styles
						lozengeSpan.style.width = originalWidth;
						lozengeSpan.style.maxWidth = originalMaxWidth;
						lozengeSpan.style.display = originalDisplay;
						lozengeSpan.style.position = originalPosition;
						lozengeSpan.style.visibility = originalVisibility;
						lozengeSpan.style.top = originalTop;
						lozengeSpan.style.left = originalLeft;
						lozengeSpan.style.minWidth = '';
						if (textSpan) {
							textSpan.style.maxWidth = originalTextMaxWidth;
							textSpan.style.width = originalTextWidth;
							textSpan.style.minWidth = originalTextMinWidth;
						}

						setContentWidth(naturalWidth);
						setIsReadyToAnimate(true);
					}
				}
			});
		}, []);

		useEffect(() => {
			// Only start animation once we have measured the content width
			if (!isReadyToAnimate) {
				return;
			}

			// Hide lozenge during initial delay
			const initialDelayTimer = setTimeout(() => {
				setHasStartedAnimation(true);
			}, initialAnimationDelay);

			// Initial delay: configurable before animation starts
			// Phase 1: 0% to 100% (first PHASE_1_DURATION_MS)
			// Phase 2: 100% to content width (next PHASE_2_DURATION_MS + PHASE_2_TRANSITION_DELAY_MS delay)
			// Phase 3: Width stays at content width, isBold transitions to false (next PHASE_3_DURATION_MS + PHASE_3_BOLD_TRANSITION_DELAY_MS delay)
			// Complete: Animation finished
			// Total animation time: initialAnimationDelay + PHASE_1_DURATION_MS + PHASE_2_DURATION_MS + PHASE_3_DURATION_MS
			const phase1Timer = setTimeout(() => {
				setPhase('phase2');
				// Use requestAnimationFrame to ensure animation has completed before starting transition
				requestAnimationFrame(() => {
					setShouldStartPhase2Transition(true);
				});
			}, initialAnimationDelay + PHASE_1_DURATION_MS);

			const phase2Timer = setTimeout(
				() => {
					setPhase('phase3');
					// Wait PHASE_3_BOLD_TRANSITION_DELAY_MS after phase3 starts before transitioning isBold (to match the transition delay)
					setTimeout(() => {
						setShouldTransitionBold(true);
					}, PHASE_3_BOLD_TRANSITION_DELAY_MS);
				},
				initialAnimationDelay + PHASE_1_DURATION_MS + PHASE_2_DURATION_MS,
			);

			const phase3Timer = setTimeout(
				() => {
					setPhase('complete');
				},
				initialAnimationDelay + PHASE_1_DURATION_MS + PHASE_2_DURATION_MS + PHASE_3_DURATION_MS,
			);

			return () => {
				clearTimeout(initialDelayTimer);
				clearTimeout(phase1Timer);
				clearTimeout(phase2Timer);
				clearTimeout(phase3Timer);
			};
		}, [isReadyToAnimate, initialAnimationDelay]);

		const currentAppearance = phase === 'phase1' || phase === 'phase2' ? 'inprogress' : appearance;
		// Keep isBold true until width animation has settled (300ms into phase3)
		const currentIsBold = !shouldTransitionBold;

		const isAnimatingPhase1 = phase === 'phase1' && isReadyToAnimate;
		const contentWidthPx = contentWidth !== null ? `${contentWidth}px` : undefined;

		// Force Lozenge to stretch during phase 1 and shrink during phase 2 by directly modifying its first span element
		useEffect(() => {
			if (!hasStartedAnimation) {
				return;
			}

			const lozengeSpan = lozengeWrapperRef.current?.querySelector('span') as HTMLElement;
			if (!lozengeSpan) {
				return;
			}

			if (phase === 'phase1' && isAnimatingPhase1) {
				// Phase 1: Stretch Lozenge to wrapper width
				// Remove maxWidth constraints to prevent truncation
				lozengeSpan.style.maxWidth = 'none';
				const textSpan = lozengeSpan.querySelector('span') as HTMLElement;
				if (textSpan) {
					textSpan.style.maxWidth = 'none';
				}

				let animationFrameId: number;
				const updateLozengeWidth = () => {
					if (animatedContainerRef.current) {
						const wrapperElement = animatedContainerRef.current.parentElement;
						const wrapperWidth = wrapperElement?.offsetWidth || 0;
						if (wrapperWidth > 0) {
							lozengeSpan.style.width = `${wrapperWidth}px`;
							lozengeSpan.style.display = 'flex';
						}
					}
					// Continue updating while in phase 1
					if (phase === 'phase1' && isAnimatingPhase1) {
						animationFrameId = requestAnimationFrame(updateLozengeWidth);
					}
				};

				animationFrameId = requestAnimationFrame(updateLozengeWidth);

				return () => {
					if (animationFrameId) {
						cancelAnimationFrame(animationFrameId);
					}
				};
			} else if (phase === 'phase2') {
				// Phase 2: Transition from wrapper width to content width, right-aligned
				if (contentWidth !== null && animatedContainerRef.current) {
					const wrapperElement = animatedContainerRef.current.parentElement;
					const wrapperWidth = wrapperElement?.offsetWidth || 0;

					// Remove maxWidth constraints to prevent truncation
					lozengeSpan.style.maxWidth = 'none';
					const textSpan = lozengeSpan.querySelector('span') as HTMLElement;
					if (textSpan) {
						textSpan.style.maxWidth = 'none';
					}

					// Set initial width to wrapper width (from phase 1) and ensure right alignment
					lozengeSpan.style.width = `${wrapperWidth}px`;
					lozengeSpan.style.display = 'flex';
					lozengeSpan.style.marginLeft = 'auto';

					// Use requestAnimationFrame to ensure the initial width is set before transition
					requestAnimationFrame(() => {
						// Transition to content width (shrinks from left to right)
						lozengeSpan.style.transition = `width ${PHASE_2_DURATION_MS}ms ${PHASE_2_TIMING_FUNCTION} ${PHASE_2_TRANSITION_DELAY_MS}ms`;
						lozengeSpan.style.width = `${contentWidth}px`;
					});
				}
			} else if (phase === 'phase3' || phase === 'complete') {
				// Phase 3 and complete: Keep content width, remove transition, maintain right alignment
				if (contentWidth !== null) {
					// Remove maxWidth constraints to prevent truncation
					lozengeSpan.style.maxWidth = 'none';
					const textSpan = lozengeSpan.querySelector('span') as HTMLElement;
					if (textSpan) {
						textSpan.style.maxWidth = 'none';
					}

					lozengeSpan.style.transition = '';
					lozengeSpan.style.width = `${contentWidth}px`;
					lozengeSpan.style.display = '';
					lozengeSpan.style.marginLeft = 'auto';
				}
			}
		}, [phase, isAnimatingPhase1, hasStartedAnimation, contentWidth]);

		// CSS variables for dynamic values - must be set via style prop
		// Only set --content-width if we have a measured value
		const cssVariables = {
			...(contentWidthPx && { '--content-width': contentWidthPx }),
			// Set animation delay dynamically when animating phase 1
			...(isAnimatingPhase1 && {
				animationDelay: `${initialAnimationDelay}ms`,
			}),
		} as CSSProperties & {
			'--content-width'?: string;
			animationDelay?: string;
		};

		return (
			<div
				css={[
					styles.wrapper,
					phase === 'phase1' && styles.wrapperPhase1,
					(phase === 'phase2' || phase === 'phase3' || phase === 'complete') &&
						styles.wrapperPhase2,
				]}
			>
				<div
					ref={animatedContainerRef}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- CSS variables for dynamic values (content-width from state, animation delay)
					style={cssVariables}
					css={[
						styles.animatedContainer,
						!hasStartedAnimation && styles.animatedContainerHidden,
						isAnimatingPhase1 && styles.animatedContainerAnimating,
						phase === 'phase2' &&
							!shouldStartPhase2Transition &&
							styles.animatedContainerPhase2Start,
						phase === 'phase2' && shouldStartPhase2Transition && styles.animatedContainerPhase2,
						phase === 'phase3' && styles.animatedContainerPhase3,
						phase === 'complete' && styles.animatedContainerComplete,
					]}
				>
					<div ref={lozengeWrapperRef}>
						<Lozenge
							appearance={currentAppearance}
							isBold={currentIsBold}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- style prop is dynamic from component props
							style={style}
							testId={testId}
						>
							{children}
						</Lozenge>
					</div>
				</div>
			</div>
		);
	},
);

AnimatedLozenge.displayName = 'AnimatedLozenge';

export default AnimatedLozenge;
